import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { FeatureSpecsEntities, FeatureSpecsPaths, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { FEATURES_SPECS_HEADER_SRC_INLINE } from 'site-modules/features-specs/constants/features-specs';
import { MoreAboutMMYLink } from 'site-modules/shared/components/more-about-mmy-link/more-about-mmy-link';
import { FeaturesSpecsTable } from 'site-modules/features-specs/components/features-specs-table/features-specs-table';
import { InventoryButton } from 'site-modules/shared/components/inventory-button/inventory-button';
import { FeaturesTrimBox } from 'site-modules/features-specs/components/features-trim-box/features-trim-box';

import './style-features-specs.scss';

function StyleFeaturesSpecsUI({
  vehicle,
  styleId = undefined,
  styleOverview = null,
  popularStyles,
  vehicleName,
  params,
  visitorLocation = null,
  onStyleChange = undefined,
  pageName = null,
  isMobile = false,
}) {
  if (!styleOverview) {
    return null;
  }
  const { isUsed } = params;

  const currentStyle = popularStyles.find(({ id }) => id === styleId);

  return (
    <div className="style-features-specs pos-r">
      <img
        className="header-background pos-a top-0 left-0 w-100"
        src={FEATURES_SPECS_HEADER_SRC_INLINE[isMobile]}
        alt=""
        fetchPriority="high"
      />
      <Container className="pt-1_5 pt-md-2 mb-2">
        <Row className="gy-1_5 mb-2">
          <Col xs={12} md={9}>
            <h1
              className={classnames('title fw-bold text-white text-shadow-contrast-high mb-0_25 mb-md-0_5', {
                'size-30': isMobile,
                'size-36': !isMobile,
              })}
            >
              {vehicleName} - Specs & Features
            </h1>
            <MoreAboutMMYLink
              vehicle={vehicle}
              className="text-white text-shadow-contrast-high"
              linkClassName={null}
              iconClassName={null}
              linkToReview={isUsed}
              showBuyOnlineBtn={isUsed}
              isMobile={isMobile}
              params={params}
            />
          </Col>
          <Col xs={12} lg={7} className="d-flex flex-column gap-1_5">
            <FeaturesTrimBox
              params={params}
              styleOverview={styleOverview}
              popularStyles={popularStyles}
              onStyleChange={onStyleChange}
            />
            {params.isUsed && (
              <div className="d-flex">
                <InventoryButton
                  params={params}
                  makeModelSubmodelYear={vehicle}
                  visitorLocation={visitorLocation}
                  className="fw-medium"
                  btnColor="blue-50"
                />
                <InventoryButton
                  params={params}
                  makeModelSubmodelYear={vehicle}
                  visitorLocation={visitorLocation}
                  className="fw-medium"
                  btnColor="blue-50"
                  isBuyOnline
                />
              </div>
            )}
          </Col>
        </Row>
        <FeaturesSpecsTable
          styleId={styleId}
          visitorLocation={visitorLocation}
          vehicle={vehicle}
          trimName={currentStyle?.trim}
          params={params}
          isMobile={isMobile}
          pageName={pageName}
        />
      </Container>
    </div>
  );
}

StyleFeaturesSpecsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  popularStyles: VehicleEntities.PopularStyles.isRequired,
  params: FeaturesSpecsParams.isRequired,
  vehicleName: PropTypes.string.isRequired,
  styleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  styleOverview: FeatureSpecsEntities.FeatureStyleOverviewEntity,
  visitorLocation: VisitorEntities.Location,
  onStyleChange: PropTypes.func,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { styleOverview }) => !styleOverview;
export const StyleFeaturesSpecs = connectToModel(React.memo(StyleFeaturesSpecsUI, propsAreEqual), {
  styleOverview: bindToPath(props => {
    const { styleId } = props; // user selection || page url || default style || undefined if parent component has not resolved popular styles yet
    if (styleId) {
      return FeatureSpecsPaths.buildFeaturesOverviewPathForStyleId(styleId);
    }
    /**
     *  we could return null instead of buildFeaturesOverviewPathForDefaultStyle(props.params)
     *  and component would work the same given the parent component resolves and passes the default styleId
     *  reason for returning buildFeaturesOverviewPathForDefaultStyle(props.params), however, is that
     *  1. it turns this into a standalone component that can be shared between pages
     *  2. would allow us to remove the complexity from above component, making the page simpler
     *  3. That path is in preload, having something in preload and not in any component would lead to confusion
     */
    return FeatureSpecsPaths.buildFeaturesOverviewPathForDefaultStyle(props.params);
  }, VehicleModel),
  visitorLocation: bindToPath('location', VisitorModel),
});
